<template>
    <div class="container-fluid">
        <div class="page-hero">
            <div>
                <p class="page-hero__eyebrow">Boost Plans</p>
                <h1>Boost Plans</h1>
                <p class="page-hero__copy">Review the boost plan catalog, pricing, visibility, and rollout settings in one polished view.</p>
            </div>
            <div class="page-hero__stat">
                <span class="page-hero__stat-label">Total plans</span>
                <strong>{{ modelCount }}</strong>
            </div>
        </div>

        <div class="account-details">
            <div class="contract-header">
                <div><span class="badge badge-primary">{{ modelCount }}</span> Plans </div>
                <div>
                    <button @click="openCreateModal" class="btn btn-primary btn-xs">Setup</button>
                </div>
            </div>
    
            <div class="table table-responsive table-sm">
                <v-data-table :headers="header" :items="desserts" dense class="table-striped table-bordered table-responsive">
                    <template v-slot:item.name="{ item }">
                        <div class="plan-name-cell">
                            <span class="badge badge-info plan-name">{{ item.name }}</span>
                            <small class="plan-role">{{ item.model_role }}</small>
                        </div>
                    </template>
                    <template v-slot:item.price="{ item }">
                        <div class="a-text price-pill">{{ formatToCurrency(item.price) }}</div>
                    </template>
                    <template v-slot:item.dayruns_token="{ item }">
                        <div class="a-text"> {{ formatToCurrency(item.dayruns_token) }}</div>
                    </template>
                    <template v-slot:item.visibility_duration="{ item }">
                        <div class="a-text"> {{ item.visibility_duration }} days</div>
                    </template>
                    <template v-slot:item.priority_level="{ item }">
                        <div class="a-text">Level {{ item.priority_level }}</div>
                    </template>
                    <template v-slot:item.featured="{ item }">
                        <div class="a-text">
                            <span v-if="Number(item.featured) === 1" class="badge badge-success">Featured</span>
                            <span v-else class="badge badge-secondary">No</span>
                        </div>
                    </template>
                    <template v-slot:item.activation_type="{ item }">
                        <div class="a-text">{{ item.activation_type }}</div>
                    </template>
                    <template v-slot:item.model_role="{ item }">
                        <div class="a-text">{{ item.model_role }}</div>
                    </template>
                    <template v-slot:item.created_at="{ item }">
                        <div class="a-text">{{ formatDate(item.created_at) }}</div>
                    </template>
                    <template v-slot:item.description="{ item }">
                        <div class="a-text plan-description">{{ truncateText(item.description, 72) }}</div>
                    </template>

                    <!-- Table Slot -->
                    <template v-slot:item.action="{ item }">
                        <div class="table-actions">
                        <button class="btn btn-success btn-xs" @click="openApprovalModal(item)">
                            View details
                        </button>
                        <button class="btn btn-outline-secondary btn-xs" @click="openEditModal(item)">
                            Edit
                        </button>
                        </div>
                    </template>
                    
                </v-data-table>
            </div>
            </div>
            
        <!-- Create Modal -->
        <div v-if="showCreateModal" class="modal-backdrop-custom">
        <div class="modal-wrapper">
            <div class="modal-subs modal-lg shadow-lg rounded-3 overflow-hidden bg-white">
            <div class="modal-header subs-content text-white">
                <h5 class="modal-title">
                <i class="bi bi-plus-circle"></i> Setup Boost Plan
                </h5>
                <button
                type="button"
                class="btn-close btn-close-white"
                @click="closeCreateModal"
                ></button>
            </div>

            <div class="modal-body p-4">
                <form @submit.prevent="saveBoostPlan">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Plan name</label>
                        <input
                            type="text"
                            v-model="boostPlanData.name"
                            class="form-control"
                            placeholder="Enter plan name"
                            required
                        />
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Token</label>
                        <input
                            type="number"
                            min="0"
                            v-model="boostPlanData.dayruns_token"
                            class="form-control"
                            placeholder="Enter token value"
                            required
                        />
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Visibility</label>
                        <select
                            v-model="boostPlanData.visibility_duration"
                            class="form-control"
                            required
                        >
                            <option value="">Select visibility</option>
                            <option :value="7">7 days</option>
                            <option :value="14">14 days</option>
                            <option :value="30">30 days</option>
                            <option :value="60">60 days</option>
                            <option :value="90">90 days</option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Activation type</label>
                        <select
                            v-model="boostPlanData.activation_type"
                            class="form-control"
                            required
                        >
                            <option value="">Select activation type</option>
                            <option value="boosting">boosting</option>
                            <option value="activation">activation</option>
                            <option value="banner">banner</option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Model role</label>
                        <select
                            v-model="boostPlanData.model_role"
                            class="form-control"
                            required
                        >
                            <option value="">Select role</option>
                            <option value="vip">vip</option>
                            <option value="regular">regular</option>
                        </select>
                    </div>
                    <div class="col-12 mb-3">
                        <label class="form-label fw-semibold">Description</label>
                        <textarea
                            v-model="boostPlanData.description"
                            class="form-control"
                            rows="5"
                            placeholder="Enter plan description"
                            required
                        ></textarea>
                    </div>
                </div>
                </form>
            </div>

            <div class="modal-footer d-flex justify-content-end bg-light px-4">
                <button
                type="button"
                class="btn btn-outline-secondary px-4 me-2"
                @click="closeCreateModal"
                >
                Cancel
                </button>
                <button
                type="button"
                class="btn btn-success px-4"
                @click="saveBoostPlan"
                >
                Save
                </button>
            </div>
            </div>
        </div>
        </div>

        <!-- Edit Modal -->
        <div v-if="showEditModal" class="modal-backdrop-custom">
        <div class="modal-wrapper">
            <div class="modal-subs modal-lg shadow-lg rounded-3 overflow-hidden bg-white">
            <div class="modal-header subs-content text-white">
                <h5 class="modal-title">
                <i class="bi bi-pencil-square"></i> Edit Boost Plan
                </h5>
                <button
                type="button"
                class="btn-close btn-close-white"
                @click="closeEditModal"
                ></button>
            </div>

            <div class="modal-body p-4">
                <form @submit.prevent="updateBoostPlan">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Plan name</label>
                        <input
                            type="text"
                            v-model="boostPlanData.name"
                            class="form-control"
                            placeholder="Enter plan name"
                            required
                        />
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Token</label>
                        <input
                            type="number"
                            min="0"
                            v-model="boostPlanData.dayruns_token"
                            class="form-control"
                            placeholder="Enter token value"
                            required
                        />
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Visibility</label>
                        <select
                            v-model="boostPlanData.visibility_duration"
                            class="form-control"
                            required
                        >
                            <option value="">Select visibility</option>
                            <option :value="1">1 day</option>
                            <option :value="3">3 days</option>
                            <option :value="7">7 days</option>
                            <option :value="14">14 days</option>
                            <option :value="30">30 days</option>
                            <option :value="60">60 days</option>
                            <option :value="90">90 days</option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Activation type</label>
                        <select
                            v-model="boostPlanData.activation_type"
                            class="form-control"
                            required
                        >
                            <option value="">Select activation type</option>
                            <option value="boosting">boosting</option>
                            <option value="activation">activation</option>
                            <option value="banner">banner</option>
                        </select>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label fw-semibold">Model role</label>
                        <select
                            v-model="boostPlanData.model_role"
                            class="form-control"
                            required
                        >
                            <option value="">Select role</option>
                            <option value="vip">vip</option>
                            <option value="regular">regular</option>
                        </select>
                    </div>
                    <div class="col-12 mb-3">
                        <label class="form-label fw-semibold">Description</label>
                        <textarea
                            v-model="boostPlanData.description"
                            class="form-control"
                            rows="5"
                            placeholder="Enter plan description"
                            required
                        ></textarea>
                    </div>
                </div>
                </form>
            </div>

            <div class="modal-footer d-flex justify-content-end bg-light px-4">
                <button
                type="button"
                class="btn btn-outline-secondary px-4 me-2"
                @click="closeEditModal"
                >
                Cancel
                </button>
                <button
                type="button"
                class="btn btn-success px-4"
                @click="updateBoostPlan"
                >
                Update
                </button>
            </div>
            </div>
        </div>
        </div>

             <!-- Approval Modal -->
        <div v-if="showApprovalModal" class="modal-backdrop-custom">
        <div class="modal-wrapper">
            <div class="modal-subs modal-lg shadow-lg rounded-3 overflow-hidden bg-white">
            <!-- Header -->
            <div class="modal-header subs-content text-white">
                <h5 class="modal-title">
                <i class="bi bi-person-check"></i> Boost Plan Details
                </h5>
                <button
                type="button"
                class="btn-close btn-close-white"
                @click="closeApprovalModal"
                ></button>
            </div>

            <!-- Body -->
            <div class="modal-body p-4">
                <!-- User Details (2 columns) -->
                <div class="user-info px-3">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Plan name:</strong>
                        <div>{{ selectedPlan.name }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Price:</strong>
                        <div>{{ formatToCurrency(selectedPlan.price) }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Tokens:</strong>
                        <div>{{ selectedPlan.dayruns_token }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Visibility:</strong>
                        <div>{{ selectedPlan.visibility_duration }} days</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Priority level:</strong>
                        <div>{{ selectedPlan.priority_level }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Featured:</strong>
                        <div>{{ Number(selectedPlan.featured) === 1 ? 'Yes' : 'No' }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Activation type:</strong>
                        <div>{{ selectedPlan.activation_type }}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <strong class="text-muted">Model role:</strong>
                        <div>{{ selectedPlan.model_role }}</div>
                    </div>
                    <div class="col-12 mb-3">
                        <strong class="text-muted">Description:</strong>
                        <div>{{ selectedPlan.description }}</div>
                    </div>
                </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer d-flex justify-content-end bg-light px-4">
                <button
                type="button"
                class="btn btn-outline-secondary px-4 me-2"
                @click="closeApprovalModal"
                >
                Cancel
                </button>
               
            </div>
            </div>
        </div>
        </div>
       
        <p>&nbsp;</p>
        <page-loader :is-loading="isLoading"></page-loader>
    </div>
    </template>
    
      
    <script>
    import swal from 'sweetalert2';
    import PageLoader from '../components/PageLoader.vue'

    export default {
    
        name: "AllUsersPage",
        components: {
            //quillEditor,
            PageLoader,
        },
    
        data() {
            return {
    
                isLoading: false,
                showCreateModal: false,
                showEditModal: false,
                isRightImage: false,
                isLeftImage: false,
                isBackImage: false,
                is_transaction_fee: true,
                is_transaction_fee_hidden: false,
                user_id: '',
                firstname: '',
                lastname: '',
                bank_name: '',
                account_name: '',
                account_number: '',
    
                loading: false,
                loading1: false,
                loading2: false,
                loading3: false,
                role_type: '',
                contract_title: '',
                transaction_type: '',
                delivery_type: '',
                delivery_location: '',
                delivery_time: '',
                contract_amount: '',
                transaction_fee: '',
                whois_paying: '',
                contract_rebate: '',
                product_description: '',
                accept_terms: '',
    
                products: [],
                subcategorys: [],
                selectedFile: '',
                selectedFile1: '',
                selectedFile2: '',
                selectedFile3: '',
                addProduct: false,
                viewProduct: true,
                editorOptions: {
                    placeholder: 'Write something...',
                },
    
                isVariations: false,
                productid: '',
                variations: '',
                vprice: '',
                is_fees_status: false,
                is_submit: false,
                is_submit1: false,
                is_submit2: false,
                is_submit3: false,
                img_url: process.env.VUE_APP_IMAGE_URL,
                fallbackImage: require('../assets/logo.png'),
                header: [
                    
                    {
                        value: 'name',
                        text: 'Plan name'
                    },          
                  
                    {
                        value: 'dayruns_token',
                        text: 'Tokens'
                    },
                    {
                        value: 'visibility_duration',
                        text: 'Visibility'
                    },
                   
                    {
                        value: 'activation_type',
                        text: 'Activation'
                    },
                    {
                        value: 'model_role',
                        text: 'Role'
                    },
                    {
                        value: 'created_at',
                        text: 'Created at'
                    },
                    {
                        value: 'description',
                        text: 'Description'
                    },
                    {
                        value: 'action',
                        text: 'Action'
                    },
                   
                ],
    
                desserts: [],
                user_role: '',
                product_id: '',
                userid: '',
                modelCount: '',
            
                modelData: [],
                isPeriod: false,
                delivery_period: '',
                payment_status: '',
                is_escrow_charge: false,
                error: '',
                error1: '',
                duration_twelve: false,
                duration_twelve1: true,
                label1: '',
                isContractLink: false,
                contractLink: '',
                service_charge_fee: '',
                boostPlanData: {
                    id: '',
                    name: '',
                    dayruns_token: '',
                    visibility_duration: '',
                    activation_type: '',
                    model_role: '',
                    description: '',
                },

                showApprovalModal: false,
                selectedPlan: {
                    name: '',
                    price: 0,
                    dayruns_token: 0,
                    visibility_duration: 0,
                    priority_level: 0,
                    featured: 0,
                    activation_type: '',
                    model_role: '',
                    created_at: '',
                    description: '',
                },
            };
        },
    
        beforeMount() {
    
        },
    
        mounted() {
    
            this.firstname = localStorage.getItem("firstname");
            this.lastname = localStorage.getItem("lastname");
            this.phone_number = localStorage.getItem("phone");
            this.email_address = localStorage.getItem("email");
            this.user_id = localStorage.getItem("userid");
    
            this.bank_name = localStorage.getItem("bank_name");
            this.account_name = localStorage.getItem("account_name");
            this.account_number = localStorage.getItem("account_number");
            this.user_role = localStorage.getItem("user_role");
    
            this.fetchAllModels()
        },
    
        created() {
    
        },
    
        computed: {
            indexSn() {
                return this.desserts.map((d, index) => ({
                    ...d,
                    sn: index + 1
                }))
            },
    
        },
    
        methods: {
            
                validateNumber() {
                const value = this.delivery_period;
                if (value < 0) {
                this.error = 'Value must be non-negative';
                this.delivery_period = ''
                return
                } else if (!Number.isInteger(Number(value))) {
                this.error = 'Value must be an integer';
                this.delivery_period = ''
                return
                } else {
                this.error = '';
                }
            },

            openCreateModal() {
                this.boostPlanData = {
                    id: '',
                    name: '',
                    dayruns_token: '',
                    visibility_duration: '',
                    activation_type: '',
                    model_role: '',
                    description: '',
                }
                this.showCreateModal = true
            },

            closeCreateModal() {
                this.showCreateModal = false
            },

            openEditModal(item) {
                this.boostPlanData = {
                    id: item.id || '',
                    name: item.name || '',
                    dayruns_token: item.dayruns_token || '',
                    visibility_duration: item.visibility_duration || '',
                    activation_type: item.activation_type || '',
                    model_role: item.model_role || '',
                    description: item.description || '',
                }
                this.showEditModal = true
            },

            closeEditModal() {
                this.showEditModal = false
            },

            formatToCurrency(value) {
              return Number(value || 0).toLocaleString('en-NG', { minimumFractionDigits: 0 })
            },

            formatDate(value) {
              if (!value) {
                return '-'
              }
              const date = new Date(value)
              return Number.isNaN(date.getTime()) ? '-' : date.toDateString()
            },

            truncateText(value, length) {
                const text = String(value || '')
                if (!text) {
                    return '-'
                }

                return text.length > length ? `${text.slice(0, length)}...` : text
            },

            getModelImageUrl(profilepic) {
                if (!profilepic) {
                    return this.fallbackImage;
                }

                if (/^https?:\/\//i.test(profilepic)) {
                    return profilepic;
                }

                const baseUrl = this.img_url || '';
                const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                const trimmedPath = String(profilepic).startsWith('/') ? String(profilepic).slice(1) : String(profilepic);

                return `${trimmedBase}/${trimmedPath}`;
            },
    
           
            copied(){
                this.isContractLink = false
            },
    
            copyToClipboard(link) {
               // var copyText = document.getElementById("myInput");
                navigator.clipboard.writeText(link).then(function() {
                    alert("Contract link copied to clipboard!");
                }).catch(function(err) {
                    console.error('Failed to copy: ', err);
                });
            },
    
            copyToClipboard1(link) {
               // var copyText = document.getElementById("myInput");
                navigator.clipboard.writeText(link).then(function() {
                    alert("Contract link copied to clipboard!");
                }).catch(function(err) {
                    console.error('Failed to copy: ', err);
                });
            },

            async saveBoostPlan() {
                try {
                    if (
                        !this.boostPlanData.name ||
                        !this.boostPlanData.dayruns_token ||
                        !this.boostPlanData.visibility_duration ||
                        !this.boostPlanData.activation_type ||
                        !this.boostPlanData.model_role ||
                        !this.boostPlanData.description
                    ) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Incomplete form',
                            text: 'Please fill in all boost plan fields.',
                        })
                        return
                    }

                    this.isLoading = true
                    const response = await this.$http.post('/create-boost-plan', {
                        name: this.boostPlanData.name,
                        dayruns_token: this.boostPlanData.dayruns_token,
                        visibility_duration: this.boostPlanData.visibility_duration,
                        activation_type: this.boostPlanData.activation_type,
                        model_role: this.boostPlanData.model_role,
                        description: this.boostPlanData.description,
                    })

                    swal.fire({
                        icon: 'success',
                        title: 'Success',
                        width: 450,
                        height: 5,
                        text: response.data.message,
                    })

                    await this.fetchAllModels()
                    this.closeCreateModal()
                } catch (err) {
                    console.log(err);
                } finally {
                    this.isLoading = false
                }
            },

            async updateBoostPlan() {
                try {
                    if (
                        !this.boostPlanData.id ||
                        !this.boostPlanData.name ||
                        !this.boostPlanData.dayruns_token ||
                        !this.boostPlanData.visibility_duration ||
                        !this.boostPlanData.activation_type ||
                        !this.boostPlanData.model_role ||
                        !this.boostPlanData.description
                    ) {
                        swal.fire({
                            icon: 'warning',
                            title: 'Incomplete form',
                            text: 'Please fill in all boost plan fields.',
                        })
                        return
                    }

                    this.isLoading = true
                    const response = await this.$http.post('/update-boost-plan', {
                        id: this.boostPlanData.id,
                        name: this.boostPlanData.name,
                        dayruns_token: this.boostPlanData.dayruns_token,
                        visibility_duration: this.boostPlanData.visibility_duration,
                        activation_type: this.boostPlanData.activation_type,
                        model_role: this.boostPlanData.model_role,
                        description: this.boostPlanData.description,
                    })

                    swal.fire({
                        icon: 'success',
                        title: 'Success',
                        width: 450,
                        height: 5,
                        text: response.data.message,
                    })

                    await this.fetchAllModels()
                    this.closeEditModal()
                } catch (err) {
                    console.log(err);
                } finally {
                    this.isLoading = false
                }
            },
            async fetchAllModels() {
                try {
                    this.isLoading = true
                    this.desserts = []
                    const response = await this.$http.get('/list-boost-plans', {
                        //user_id: this.user_id
                    })
                    this.isLoading = false
                    
                    //let photo_url = '';
                    this.modelData = response.data.data
                    this.modelCount = this.modelData.length
                    this.modelData.forEach(el => {
                        this.desserts.push({
                            id: el.id,
                            name: el.name,
                            price: el.price,
                            dayruns_token: el.dayruns_token,
                            visibility_duration: el.visibility_duration,
                            description: el.description,
                            created_at: el.created_at,
                            priority_level: el.priority_level,
                            featured: el.featured,
                            activation_type: el.activation_type,
                            model_role: el.model_role,
                        })
                    });
    
                } catch (err) {
                    console.log(err);
                } finally {
                    this.isLoading = false;
                }
            },

            openApprovalModal(user) {
            //console.log(user)
            this.showApprovalModal = true;
            this.selectedPlan = {
                name: user.name || '',
                price: user.price || 0,
                dayruns_token: user.dayruns_token || 0,
                visibility_duration: user.visibility_duration || 0,
                priority_level: user.priority_level || 0,
                featured: user.featured || 0,
                activation_type: user.activation_type || '',
                model_role: user.model_role || '',
                created_at: user.created_at || '',
                description: user.description || '',
            };
           
            },
            closeApprovalModal() {
            this.showApprovalModal = false;
            },
            approveUser(user) {
            // Handle approval logic here
              console.log("User approved:", user);

            },
    
        }
    
    }
    </script>
      
    <style lang="scss" scoped>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;700&display=swap');
    .container-fluid {
        min-height: 100vh;
        padding: 24px;
        background:
            radial-gradient(circle at top left, rgba(211, 62, 116, 0.12), transparent 30%),
            radial-gradient(circle at top right, rgba(8, 41, 74, 0.10), transparent 26%),
            linear-gradient(180deg, #f8fafc 0%, #eef3f8 100%);
        font-family: Poppins;
    }
    .page-hero {
        display: flex;
        justify-content: space-between;
        align-items: end;
        gap: 20px;
        margin-bottom: 18px;
        padding: 24px 26px;
        border-radius: 24px;
        background: linear-gradient(135deg, rgba(13, 23, 42, 0.96), rgba(35, 47, 68, 0.94));
        color: #fff;
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.14);
    }
    .page-hero__eyebrow {
        margin: 0 0 6px;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        font-size: 12px;
        color: rgba(255, 255, 255, 0.72);
    }
    .page-hero h1 {
        margin: 0;
        font-size: clamp(1.5rem, 2.5vw, 2.3rem);
        font-weight: 700;
    }
    .page-hero__copy {
        margin: 10px 0 0;
        max-width: 58ch;
        color: rgba(255, 255, 255, 0.8);
        line-height: 1.6;
    }
    .page-hero__stat {
        min-width: 180px;
        padding: 16px 18px;
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.12);
        text-align: right;
    }
    .page-hero__stat-label {
        display: block;
        margin-bottom: 4px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: rgba(255, 255, 255, 0.7);
    }
    .page-hero__stat strong {
        font-size: 2rem;
        line-height: 1;
    }
    .text-header {
        margin: 0 0 18px;
        padding: 18px 22px;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
        color: #081b2d;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }
    .account-details {
        width: 100%;
        border-radius: 24px;
        padding: 22px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
    }
    .contract-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 0 0 16px;
        margin-bottom: 16px;
        background: transparent;
        border-bottom: 1px solid #e8edf3;
        color: #081b2d;
        border-radius: 0;
        font-size: 14px;
        font-weight: 700;
    }
    .v-data-table {
        font-family: Poppins;
    }
    .v-data-table ::v-deep thead th {
        background: #f8fafc;
        color: #334155;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
    }
    .v-data-table ::v-deep tbody td {
        font-size: 13px;
        color: #0f172a;
        vertical-align: middle;
    }
    .a-text {
        font-size: 13px;
        color: #334155;
        font-family: Poppins;
    }
    .table-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }
    .plan-name-cell {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .plan-name {
        width: fit-content;
        padding: 0.45rem 0.7rem;
        border-radius: 999px;
        font-size: 12px;
        white-space: normal;
        text-align: left;
    }
    .plan-role {
        font-size: 12px;
        color: #64748b;
        text-transform: capitalize;
    }
    .plan-description {
        max-width: 320px;
        line-height: 1.5;
    }
    .model-cell {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .model-avatar {
        width: 52px;
        height: 52px;
        border-radius: 16px;
        object-fit: cover;
        border: 2px solid #fff;
        box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
        background: #e2e8f0;
    }
    .model-cell__text {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .model-name {
        width: fit-content;
        padding: 0.45rem 0.7rem;
        border-radius: 999px;
        font-size: 12px;
    }
    .model-type {
        font-size: 12px;
        color: #64748b;
    }
    .price-pill {
        display: inline-flex;
        align-items: center;
        padding: 0.35rem 0.65rem;
        border-radius: 999px;
        background: rgba(211, 62, 116, 0.1);
        color: #b02f60;
        font-weight: 600;
    }
    .modal-wrapper {
        max-width: 680px;
    }
    .modal-subs {
        border-radius: 24px;
    }
    /* Dark backdrop */
/* Backdrop */
    .modal-backdrop-custom {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
    }

    /* Modal wrapper (center container) */
    .modal-wrapper {
    width: 100%;
    max-width: 600px;
    }

    /* Header */
    .subs-content {
    padding: 0.5rem;
    background-color: #D33E74; /* Bootstrap success green */
    }
    .btn-success{
        background-color: #D33E74; 
        border: 0px;
    }
    .btn-xs{
        padding: 0.2rem;
        border: 0px;
        font-family: Poppins;
        font-size: 12px;
        font-weight: 500;
    }
    .btn-outline-secondary.btn-xs {
        border: 1px solid #d33e74;
        color: #d33e74;
        background: transparent;
    }
    .btn-outline-secondary.btn-xs:hover {
        background: #d33e74;
        color: #fff;
    }
    .modal-footer{
        padding: 0.5rem;
    }
    /* User info */
    .user-info p {
    font-size: 1rem;
    color: #222;
    border-bottom: 1px solid #f1f1f1;
    padding-bottom: 8px;
    }
    .user-info p:last-child {
    border-bottom: none;
    }

    .user-info strong {
  display: block;
  font-size: 0.9rem;
  margin-bottom: 4px;
  color: #6c757d;
}
.user-info div {
  font-size: 1rem;
  color: #212529;
  font-weight: 500;
}

    .error {
        color: #fff;
      }
      .error1 {
        color: #fff;
      }
    .quill-container {
        height: 200px;
        /* Set the desired height here */
    }
    .copy-refid{
        cursor: pointer;
        font-size: 16px;
    }
    .copy-refid1{
        cursor: pointer;
        font-size: 15px;
        font-weight: 600;
    }
    .a-text {
        font-size: 12px;
        font-family: Poppins;
    }
    .a-text1 {
        font-size: 14px;
        font-family: Poppins;
    }
    .process-payment{
        cursor:pointer;
    
    }
    label {
        font-size: 12px;
        font-family: Poppins;
        font-weight: normal;
    }
    
    .inputs {
        margin-top: 20px;
        border-radius: 3px;
        font-size: 12px;
    }
    
    .price-fee {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    
        div {
            margin: 4px;
            width: 100%;
    
        }
    }
    
    .adjust-fee-input {
        // width: 100px;
        text-align: center;
    }
    
    .whopays-fee-input {
        //width: 100% !important;
    }
    
    .shop-detail {
        font-size: 12px;
        font-family: Poppins;
    }
    
    .btn-danger {
        text-transform: uppercase;
        font-size: 13px;
        font-family: Poppins;
        background: #D33E74;
        border: 0px;
        height: 45px;
    }
    
    .text-header {
        padding: 0.5rem;
        font-size: 14px;
        font-weight: bold;
        font-family: Poppins;
        background: #ccc;
    }
    
    .contract-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        background: #D33E74;
        color: #fff;
        border-radius: 4px;
        padding: 0.8rem;
        font-size: 14px;
        font-weight: 700;
        font-family: Poppins;
        margin-bottom: 5px;
    }
    
    .welcome {
        font-family: Poppins;
        font-size: 20px;
        font-weight: 700;
        margin-top: 15px;
        color: #170e0fac;
    }
    
    .account-details {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        //padding: 0.9rem;
        background: #fff;
        width: 98%;
    }
    
    .account-balance {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        background: #780712;
        color: #fff;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        font-family: Poppins;
        padding: 0.4rem;
        padding-left: 2rem;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    }
    
    .wallet-detail {
        padding-top: 1rem;
        line-height: 1.4px;
        font-size: 15px;
    
        h4 {
            font-weight: 650;
        }
    
        .acct-d {
            line-height: 0.8;
            cursor: pointer;
            text-decoration: underline;
        }
    }
    
    .wallet-icon {
        font-size: 60px
    }
    
    .fund-account {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        background: #076378;
        color: #fff;
        width: 100%;
        height: 100%;
        border-radius: 10px;
        font-family: Poppins;
        padding: 0.4rem;
        padding-left: 2rem;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    }
    
    .inputs-field {
        font-size: 14px;
        font-family: Poppins;
        height: 45px;
    }
    
    .content-center {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border: solid 1px #170e0fac;
        border-radius: 4px;
        padding: 0.5rem;
        margin: 4px;
    }
    
    .box1 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .image-input {
        margin-top: 10px;
    }
    
    .box-icon {
        padding-left: 0.4rem;
        font-size: 35px;
        color: #637807;
    }
    
    .box-details {
        font-size: 14px;
        color: #170e0fac;
    }
    
    .box1:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .box2 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .box2:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .box3 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .box3:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .box4 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .box4:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .box5 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .box5:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .box6 {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
        font-family: Poppins;
    
        a {
            text-decoration: none;
            color: #000;
        }
    }
    
    .box6:hover {
        transform: scale(1.1);
        transition: transform 0.3s ease;
        box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3), 0 6px 20px 0 rgba(0, 0, 0, 0.24);
        cursor: pointer;
    }
    
    .img {
        border-top-right-radius: 10px;
        border-top-left-radius: 10px;
    }
    
    .box-details {
        padding: 0.4rem;
        line-height: 1.4px;
    
        .title {
            font-family: Poppins;
            font-weight: 550;
            font-size: 10px;
            text-transform: capitalize;
            color: #ff4d63;
        }
    
        .description {
            font-family: Poppins;
            font-size: 12px;
        }
    }
    
    .continue {
        display: flex;
        flex-direction: row;
        justify-content: right;
        padding-right: 0.4rem;
        font-size: 30px;
        color: rgb(237, 138, 108);
        margin-right: 10px;
        margin-top: -10px;
        margin-bottom: 10px;
    }
    
    .modal-mask {
        position: fixed;
        //z-index: 9998;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, .5);
        display: table;
        transition: opacity .4s ease;
    
        &>div {
            width: 90%;
            height: auto;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: #fff;
            ;
            backdrop-filter: blur(13px);
            border-radius: 10px;
            box-sizing: border-box;
            font-family: Poppins;
        }
    
        h3 {
            margin-top: 10px;
            font-size: 18px;
            color: #000;
            font-weight: bold;
            font-family: Poppins;
        }
    }
    
    .account-content {
        display: flex;
        flex-direction: column;
        justify-content: center;
    }
    
    .account-content-h {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 50px;
    
        padding-top: 1.3rem;
        text-transform: uppercase;
        text-align: center;
        //text-decoration: underline;
    }
    
    .add-fund {
        font-size: 12px;
        text-decoration: none !important;
        text-transform: capitalize;
    }
    
    .account-content-d {
        padding-top: 1.3rem;
        font-size: 14px;
        margin-bottom: 10px;
    }
    
    .btn-secondary {
        text-transform: uppercase;
    }
    
    .modal-close {
        color: #000;
        line-height: 50px;
        font-size: 140%;
        position: absolute;
        right: 0;
        text-align: center;
        top: 2;
        width: 70px;
        text-decoration: none;
    
        &:hover {
            color: black;
        }
    }
    
    .modal-close-mobile {
        color: #000;
        line-height: 50px;
        font-size: 140%;
        position: absolute;
        right: 0;
        text-align: center;
        top: 2;
        width: 70px;
        text-decoration: none;
    
        &:hover {
            color: black;
        }
    
        display: none;
    }
    
    a {
        text-decoration: none;
    }
    
    .shop-name {
        //font-weight: 550;
        color: #000;
        font-family: Poppins;
    }

    .text-header {
        margin: 0 0 18px;
        padding: 18px 22px;
        border-radius: 20px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
        color: #081b2d;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }
    .contract-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 0 0 16px;
        margin-bottom: 16px;
        background: transparent;
        border-bottom: 1px solid #e8edf3;
        color: #081b2d;
        border-radius: 0;
        font-size: 14px;
        font-weight: 700;
    }
    .account-details {
        width: 100%;
        border-radius: 24px;
        padding: 20px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
    }
    .v-data-table {
        font-family: Poppins;
    }
    .v-data-table ::v-deep thead th {
        background: #f8fafc;
        color: #334155;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        white-space: nowrap;
    }
    .v-data-table ::v-deep tbody td {
        font-size: 13px;
        color: #0f172a;
        vertical-align: middle;
    }
    .a-text {
        font-size: 13px;
        color: #334155;
        font-family: Poppins;
    }
    
    @media screen and (max-width: 768px) {
        .container-fluid {
            padding: 14px;
        }

        .page-hero {
            flex-direction: column;
            align-items: flex-start;
            padding: 18px 18px;
            border-radius: 20px;
        }

        .page-hero__stat {
            width: 100%;
            text-align: left;
        }

        .text-header {
            padding: 14px 16px;
            border-radius: 16px;
        }

        .account-details {
            padding: 14px;
            border-radius: 18px;
        }

        .model-avatar {
            width: 44px;
            height: 44px;
            border-radius: 14px;
        }

        .contract-header {
            flex-direction: column;
            align-items: stretch;
        }

        .copy-refid1{
            cursor: pointer;
            font-size: 14px;
            font-weight: 600;
        }
        .inputs-field {
            font-size: 12px;
            font-family: Poppins;
            font-weight: normal;
            height: 45px;
        }
        .contract-header {
    
            font-size: 12px !important;
    
        }
    
        .counter {
            font-size: 13px;
            font-weight: 500 !important;
        }
    
        .welcome {
            font-family: Poppins;
            font-size: 16px;
            font-weight: 700;
            text-align: left;
            color: #170e0fac;
        }
    
        .content-center {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 0.9rem;
        }
    
        .image-input {
            margin-top: 20px;
        }
    
        .price-fee {
            display: flex;
            flex-direction: column;
    
            div {
                width: 100%;
            }
        }
        .margin-mobile{
            margin-top: 90px;;
        }
    
        .adjust-fee-input {
            width: 100%;
        }
    
        .account-details {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            width: 100%;
        }
    
        .account-balance {
            margin-bottom: 10px;
        }
    
        .box-details {
            padding: 0.4rem;
            line-height: 1.4px;
    
            .title {
                font-family: Poppins;
                font-weight: 550;
                font-size: 8px;
                text-transform: capitalize;
                color: #ff4d63;
            }
    
            .description {
                font-family: Poppins;
                font-size: 12px;
            }
        }
    
        .inputs {
            font-size: 12px;
        }
    
        .box1 {
            width: 100%;
            height: 100%;
            border-radius: 10px;
            margin-bottom: 10px;
        }
    
        .box2 {
            width: 100%;
            height: 100%;
            border-radius: 10px;
            margin-bottom: 10px;
        }
    
        .box3 {
            width: 100%;
            height: 100%;
            border-radius: 10px;
            margin-bottom: 10px;
        }
    
        .box4 {
            width: 100%;
            height: 100%;
            border-radius: 10px;
            margin-bottom: 10px;
        }
    
        .box5 {
            width: 100%;
            height: 100%;
            border-radius: 10px;
            margin-bottom: 10px;
        }
    
        .continue {
            display: flex;
            flex-direction: row;
            justify-content: right;
            padding-right: 0.4rem;
            font-size: 30px;
            margin-top: -10px;
            color: rgb(237, 138, 108);
        }
    
        .modal-close-mobile {
            display: block;
        }
    
        .modal-close {
            display: none;
        }
    
        .account-content {
            margin: 0px;
            margin-right: 0px;
        }
    
    }

    /* Final overrides for the redesigned subscription view */
    .container-fluid .text-header {
        display: none;
    }
    .container-fluid .account-details {
        display: block;
        width: 100%;
        border-radius: 24px;
        padding: 22px;
        background: rgba(255, 255, 255, 0.92);
        border: 1px solid rgba(15, 23, 42, 0.08);
        box-shadow: 0 18px 45px rgba(15, 23, 42, 0.08);
    }
    .container-fluid .contract-header {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        padding: 0 0 16px;
        margin-bottom: 16px;
        background: transparent;
        border-bottom: 1px solid #e8edf3;
        color: #081b2d;
        border-radius: 0;
        font-size: 14px;
        font-weight: 700;
        font-family: Poppins;
    }
    .container-fluid .contract-header .badge {
        padding: 0.45rem 0.7rem;
        border-radius: 999px;
    }
    </style>
    
